import { FileUploader as BaseFileUploader, FileUploaderProps } from 'baseui/file-uploader';
import { isArray, isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { MAX_UPLOAD_SIZE } from '../constants';
import { colors } from '../globalStyles';
import { borderColor } from '../utils';
import { Icon } from './Icons';
import { ErrorContainer } from './ProviderNetwork/Styles';

export type UploaderProps = Omit<FileUploaderProps, 'multiple' | 'overrides'> & {
  dropPrompt?: string;
};

/**
 * The extensions to name in the hint under the drop zone: each `accept` entry
 * reduced to its last segment (`image/png` and `.png` both read `.png`),
 * lowercased and de-duplicated. Every entry is kept, so the hint names exactly
 * the types the dropzone takes.
 */
const acceptToExtensions = (accept: readonly string[]): string[] => [
  ...new Set(
    accept.map(type => `.${(type.split('/').pop() ?? type).replace(/^\./, '').toLowerCase()}`)
  ),
];

export const FileUploader = ({
  accept,
  maxSize,
  errorMessage,
  onRetry,
  dropPrompt = 'Drop a file here',
  ...props
}: UploaderProps) => {
  const acceptedExtensions = useMemo(
    () => (isArray(accept) ? acceptToExtensions(accept) : []),
    [accept]
  );

  return (
    <>
      <BaseFileUploader
        multiple={false}
        maxSize={MAX_UPLOAD_SIZE}
        onRetry={() => onRetry?.()}
        overrides={{
          Root: { style: { color: colors.black } },
          FileDragAndDrop: {
            style: p => ({
              borderRadius: '4px',
              backgroundColor: colors.white,
              ...borderColor(p.$isDragActive ? colors.primary : colors.grey.border),
            }),
          },
          ContentMessage: () => (
            <div>
              {errorMessage ? (
                <ErrorContainer>{errorMessage}</ErrorContainer>
              ) : (
                <>
                  <Icon icon="iconsCloudUploadSvg" alt="Upload " className="center db" />
                  <p>{dropPrompt}</p>
                </>
              )}
            </div>
          ),
          ButtonComponent: {
            component: (p: any) => {
              return (
                <button
                  onClick={p.onClick}
                  style={{ color: colors.primary, fontWeight: 'bold', textDecoration: 'none' }}
                  type="button"
                  className="f5 link white bg-inherit bw0 pointer"
                >
                  {errorMessage ? 'Retry upload' : 'click to browse files'}
                </button>
              );
            },
          },
        }}
        accept={accept}
        {...props}
      />
      {!isEmpty(acceptedExtensions) && (
        <AcceptedFileTypes className="f6">
          {`Accepted file types: ${acceptedExtensions.join(', ')}`}
        </AcceptedFileTypes>
      )}
    </>
  );
};

const AcceptedFileTypes = styled.p`
  width: fit-content;
  color: ${colors.grey.dark};
  margin: 12px auto 0px auto;
`;
